/**
 * DebugSyncModal — admin diagnostic tool for a customer's Tally agent.
 *
 * Flow:
 *   1. Admin picks a step (cash / debtors / creditors / pl / inventory / gst /
 *      version / raw_xml).
 *   2. We POST /api/tally/debug-sync/{customer_id} → returns request_id.
 *   3. The agent's next heartbeat (≤5 min) picks up the request, runs the
 *      envelope against the local Tally, POSTs raw XML back to the server.
 *   4. We poll /debug-sync-status/{request_id} every 4s until status is
 *      'completed' or 'failed', then render the raw XML response with
 *      bytes / duration / error metadata.
 *
 * Also surfaces the last 20 debug requests for this customer so the admin
 * can re-open any previous run without re-queueing.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, Play, RefreshCw, Copy, Check } from "lucide-react";
import { adminApi } from "./adminApi";

const STEPS = [
  { id: "probe",           label: "4-stage probe — isolate Tally-hung vs TDL-template bug ⭐" },
  { id: "ping",            label: "Ping — built-in Company Collection envelope" },
  { id: "list_companies",  label: "List Companies — TDL Collection (universal)" },
  { id: "version",         label: "Version detect — TDL License Info" },
  { id: "cash",            label: "Cash & Bank ledgers (TDL Collection)" },
  { id: "debtors",         label: "Outstanding receivables" },
  { id: "creditors",       label: "Outstanding payables" },
  { id: "pl",              label: "Profit & Loss" },
  { id: "inventory",       label: "Stock summary" },
  { id: "gst",             label: "Duties & Taxes (GST)" },
  { id: "diagnostic",      label: "Full diagnostic — all 6 reports + parser + payload (agent ≥ 1.3.0)" },
  { id: "raw_xml",         label: "Custom raw XML envelope…" },
];

const POLL_MS    = 4_000;
const POLL_LIMIT = 90; // 90 × 4s = 6 minutes — slightly longer than heartbeat cadence

const card = { background: "white", border: "1px solid #E5E7EB", borderRadius: 8 };

function fmtBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fmtAgo(iso) {
  if (!iso) return "—";
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

function StatusPill({ status }) {
  const map = {
    pending:   { bg: "#FEF3C7", fg: "#92400E", label: "Pending — waiting for agent ping" },
    claimed:   { bg: "#DBEAFE", fg: "#1E40AF", label: "Agent picked up · running…" },
    completed: { bg: "#DCFCE7", fg: "#166534", label: "Completed" },
    failed:    { bg: "#FEE2E2", fg: "#B91C1C", label: "Failed" },
    expired:   { bg: "#F3F4F6", fg: "#374151", label: "Expired" },
  };
  const m = map[status] || { bg: "#F3F4F6", fg: "#374151", label: status || "—" };
  return (
    <span style={{
      background: m.bg, color: m.fg, padding: "2px 10px", borderRadius: 12,
      fontSize: 11, fontWeight: 700, display: "inline-block",
    }}>
      {m.label}
    </span>
  );
}

export default function DebugSyncModal({ customer, onClose }) {
  const [step, setStep] = useState("cash");
  const [rawXml, setRawXml] = useState("");
  const [queuing, setQueuing] = useState(false);
  const [queueError, setQueueError] = useState(null);
  const [active, setActive] = useState(null);   // currently watched request
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);
  // Instant diagnostic (uses last received payload — no agent wait)
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagError, setDiagError] = useState(null);
  const pollTimer = useRef(null);
  const pollCount = useRef(0);

  const loadHistory = useCallback(async () => {
    if (!customer?.id) return;
    const res = await adminApi.tallyDebugSyncList(customer.id);
    if (res.ok) setHistory(res.data?.requests || []);
  }, [customer?.id]);

  const loadDiagnose = useCallback(async () => {
    if (!customer?.id) return;
    setDiagLoading(true);
    setDiagError(null);
    const res = await adminApi.tallyDiagnose(customer.id);
    setDiagLoading(false);
    if (!res.ok) {
      setDiagError(res.error || "Failed to load diagnostic.");
      return;
    }
    setDiag(res.data);
  }, [customer?.id]);

  useEffect(() => { loadDiagnose(); }, [loadDiagnose]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  useEffect(() => () => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
  }, []);

  const pollOnce = useCallback(async (rid) => {
    const res = await adminApi.tallyDebugSyncStatus(rid);
    if (!res.ok) {
      setActive((prev) => prev ? { ...prev, _pollError: res.error } : prev);
      return false;
    }
    const row = res.data;
    setActive(row);
    if (row.status === "completed" || row.status === "failed" || row.status === "expired") {
      loadHistory();
      return true;
    }
    return false;
  }, [loadHistory]);

  const startPolling = useCallback((rid) => {
    pollCount.current = 0;
    const tick = async () => {
      pollCount.current += 1;
      const done = await pollOnce(rid);
      if (done || pollCount.current >= POLL_LIMIT) return;
      pollTimer.current = setTimeout(tick, POLL_MS);
    };
    tick();
  }, [pollOnce]);

  const handleQueue = async () => {
    setQueuing(true);
    setQueueError(null);
    if (pollTimer.current) clearTimeout(pollTimer.current);
    const xml = step === "raw_xml" ? rawXml.trim() : "";
    if (step === "raw_xml" && !xml) {
      setQueueError("Raw XML body is required when 'Custom raw XML envelope' is selected.");
      setQueuing(false);
      return;
    }
    const res = await adminApi.tallyDebugSyncQueue(customer.id, step, xml || null);
    setQueuing(false);
    if (!res.ok) {
      const d = res.data?.detail;
      if (d && typeof d === "object" && d.error === "migration_pending") {
        setQueueError(`${d.hint || "Apply tally_debug_sync.sql in Supabase."}`);
      } else {
        setQueueError(res.error || "Failed to queue request.");
      }
      return;
    }
    const rid = res.data?.request_id;
    setActive({ id: rid, step, status: "pending", requested_at: new Date().toISOString() });
    loadHistory();
    startPolling(rid);
  };

  const handleReopen = async (row) => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
    const res = await adminApi.tallyDebugSyncStatus(row.id);
    if (res.ok) setActive(res.data);
    if (res.ok && (res.data.status === "pending" || res.data.status === "claimed")) {
      startPolling(row.id);
    }
  };

  const copyResponse = async () => {
    if (!active?.response_text) return;
    try {
      await navigator.clipboard.writeText(active.response_text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) { /* noop */ }
  };

  if (!customer) return null;
  return (
    <div
      data-testid="tally-debug-sync-modal"
      style={{
        position: "fixed", inset: 0, background: "rgba(15,27,45,0.7)", zIndex: 80,
        display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#F8FAFC", width: "100%", maxWidth: 1080, borderRadius: 12, overflow: "hidden", marginTop: 24, maxHeight: "90vh", display: "flex", flexDirection: "column" }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: "1px solid #E5E7EB", background: "#0F1B2D", color: "white" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: 0.2 }}>Tally agent · debug sync</div>
            <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>
              {customer.name} <span style={{ color: "#64748B" }}>·</span> {customer.company_name || "—"}
            </div>
          </div>
          <button data-testid="debug-sync-close" onClick={onClose} style={{ background: "transparent", border: "none", color: "#94A3B8", cursor: "pointer" }}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
          {/* Instant diagnostic — pulled from last received agent payload */}
          <DiagnosticPanel
            diag={diag}
            loading={diagLoading}
            error={diagError}
            onRefresh={loadDiagnose}
          />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 16 }}>
          {/* Left — picker + recent runs */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ ...card, padding: 14 }}>
              <div style={{ fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Queue a new request</div>
              <div style={{ fontSize: 11.5, color: "#475569", marginTop: 4 }}>
                The agent will pick this up on its next ~5-min heartbeat.
              </div>

              <label style={{ display: "block", marginTop: 12, fontSize: 11, fontWeight: 700, color: "#0F1B2D" }}>Step</label>
              <select
                data-testid="debug-sync-step"
                value={step}
                onChange={(e) => setStep(e.target.value)}
                style={{ marginTop: 4, width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #D1D5DB", fontSize: 12.5 }}
              >
                {STEPS.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>

              {step === "raw_xml" && (
                <div style={{ marginTop: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "#0F1B2D" }}>XML envelope</label>
                  <textarea
                    data-testid="debug-sync-rawxml"
                    rows={7}
                    value={rawXml}
                    onChange={(e) => setRawXml(e.target.value)}
                    placeholder="<ENVELOPE>…</ENVELOPE>"
                    spellCheck={false}
                    style={{
                      marginTop: 4, width: "100%", padding: 8, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      fontSize: 11.5, border: "1px solid #D1D5DB", borderRadius: 6, resize: "vertical",
                    }}
                  />
                </div>
              )}

              {queueError && (
                <div data-testid="debug-sync-queue-error" style={{ marginTop: 10, padding: "8px 10px", background: "#FEE2E2", color: "#B91C1C", borderRadius: 6, fontSize: 11.5, lineHeight: 1.4 }}>
                  {queueError}
                </div>
              )}

              <button
                data-testid="debug-sync-queue-btn"
                onClick={handleQueue}
                disabled={queuing}
                style={{
                  marginTop: 12, width: "100%", padding: "9px 12px", borderRadius: 6, border: "none",
                  background: queuing ? "#94A3B8" : "#10B981", color: "white", fontSize: 12.5, fontWeight: 700,
                  cursor: queuing ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}
              >
                <Play size={13} /> {queuing ? "Queuing…" : "Run debug sync"}
              </button>
            </div>

            <div style={{ ...card, padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "10px 14px", borderBottom: "1px solid #E5E7EB", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Recent runs</div>
                <button onClick={loadHistory} title="Refresh" style={{ background: "transparent", border: "none", cursor: "pointer", color: "#64748B" }}>
                  <RefreshCw size={13} />
                </button>
              </div>
              <div style={{ maxHeight: 320, overflowY: "auto" }}>
                {history.length === 0 && (
                  <div style={{ padding: 14, fontSize: 12, color: "#94A3B8", textAlign: "center" }}>No debug runs yet.</div>
                )}
                {history.map((r) => (
                  <div
                    key={r.id}
                    data-testid={`debug-sync-history-${r.id}`}
                    onClick={() => handleReopen(r)}
                    style={{
                      padding: "9px 14px", borderTop: "1px solid #F1F5F9", cursor: "pointer",
                      background: active?.id === r.id ? "#F0FDF4" : "transparent",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: "#0F1B2D" }}>{r.step}</div>
                      <StatusPill status={r.status} />
                    </div>
                    <div style={{ fontSize: 11, color: "#64748B", marginTop: 3 }}>
                      {fmtAgo(r.requested_at)} · {fmtBytes(r.response_bytes)} · {r.duration_ms ? `${r.duration_ms}ms` : "—"}
                    </div>
                    {r.error_message && (
                      <div style={{ fontSize: 11, color: "#B91C1C", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.error_message}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right — active response viewer */}
          <div style={{ display: "flex", flexDirection: "column", minHeight: 400 }}>
            <div style={{ ...card, padding: 14, marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Active request</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#0F1B2D", marginTop: 4 }}>
                    {active ? active.step : "—"}
                  </div>
                </div>
                {active && <StatusPill status={active.status} />}
              </div>
              {active && (
                <div style={{ marginTop: 8, fontSize: 11.5, color: "#475569", display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <span>Requested: {fmtAgo(active.requested_at)}</span>
                  {active.completed_at && <span>· Completed: {fmtAgo(active.completed_at)}</span>}
                  {active.duration_ms != null && <span>· Tally: {active.duration_ms}ms</span>}
                  <span>· Response: {fmtBytes(active.response_bytes)}</span>
                </div>
              )}
              {active?.error_message && (
                <div data-testid="debug-sync-error" style={{ marginTop: 10, padding: "10px 12px", background: "#FEE2E2", color: "#B91C1C", borderRadius: 6, fontSize: 12, lineHeight: 1.5 }}>
                  <b>Error:</b> {active.error_message}
                </div>
              )}
              {active?._pollError && (
                <div style={{ marginTop: 10, padding: "8px 10px", background: "#FEF3C7", color: "#92400E", borderRadius: 6, fontSize: 11.5 }}>
                  Poll error: {active._pollError}
                </div>
              )}
            </div>

            <div style={{ ...card, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <div style={{ padding: "10px 14px", borderBottom: "1px solid #E5E7EB", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Raw Tally XML response</div>
                <button
                  data-testid="debug-sync-copy"
                  onClick={copyResponse}
                  disabled={!active?.response_text}
                  title="Copy to clipboard"
                  style={{
                    background: "transparent", border: "1px solid #D1D5DB", borderRadius: 5,
                    padding: "3px 8px", fontSize: 11, fontWeight: 600,
                    color: active?.response_text ? "#0F1B2D" : "#9CA3AF",
                    cursor: active?.response_text ? "pointer" : "not-allowed",
                    display: "inline-flex", alignItems: "center", gap: 4,
                  }}
                >
                  {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
                </button>
              </div>
              <pre
                data-testid="debug-sync-raw-xml"
                style={{
                  flex: 1, margin: 0, padding: 14, background: "#0F172A", color: "#E2E8F0",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: 11.5, lineHeight: 1.55, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all",
                }}
              >
                {active?.response_text
                  ? active.response_text
                  : (active
                      ? (active.status === "pending"
                          ? "Waiting for the customer's Tally agent to ping (≤5 minutes)…"
                          : active.status === "claimed"
                          ? "Agent picked up the request — executing against Tally now…"
                          : "(empty response)")
                      : "Queue a step on the left to start.")}
              </pre>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// DiagnosticPanel — renders the /api/tally/diagnose/{customer_id} response.
// Pinpoints whether all-zero syncs are caused by:
//   A. XML request    B. XML parser    C. Payload map    D. Supabase map
// ---------------------------------------------------------------------------
const VERDICT_STYLES = {
  OK:   { bg: "#DCFCE7", fg: "#166534", label: "Healthy" },
  A:    { bg: "#FEE2E2", fg: "#B91C1C", label: "A · XML request" },
  B:    { bg: "#FEE2E2", fg: "#B91C1C", label: "B · XML parser" },
  C:    { bg: "#FEF3C7", fg: "#92400E", label: "C · Payload map" },
  D:    { bg: "#FEE2E2", fg: "#B91C1C", label: "D · Supabase map" },
  NONE: { bg: "#F3F4F6", fg: "#374151", label: "No data yet" },
};

function fmtINR(n) {
  if (n == null) return "—";
  const v = Number(n);
  if (Number.isNaN(v)) return String(n);
  return new Intl.NumberFormat("en-IN").format(v);
}

function DiagnosticPanel({ diag, loading, error, onRefresh }) {
  if (loading && !diag) {
    return (
      <div style={{ ...card, padding: 14, marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: "#64748B" }}>Loading diagnostic…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ ...card, padding: 14, marginBottom: 16, borderColor: "#FECACA" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 12.5, color: "#B91C1C" }}>Diagnostic failed: {error}</div>
          <button onClick={onRefresh} style={{ background: "transparent", border: "1px solid #D1D5DB", borderRadius: 5, padding: "3px 10px", fontSize: 11.5, cursor: "pointer" }}>
            Retry
          </button>
        </div>
      </div>
    );
  }
  if (!diag) return null;

  const v = diag.verdict || {};
  const vs = VERDICT_STYLES[v.code] || VERDICT_STYLES.NONE;
  const c = diag.counts || {};
  const p = diag.parsed_values || {};
  const s = diag.supabase_snapshot || {};
  const cfg = diag.config || {};
  const ledgers = diag.first_20_ledgers || [];
  const errs = diag.parser_errors || {};

  return (
    <div data-testid="tally-diagnostic-panel" style={{ ...card, marginBottom: 16, overflow: "hidden" }}>
      <div style={{ padding: "12px 16px", background: "#0F1B2D", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 11, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Instant Tally Diagnostic</div>
          <div style={{ fontSize: 12.5, marginTop: 2, color: "#CBD5E1" }}>
            Reads the last payload the agent pushed — no agent ping needed.
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span data-testid="tally-diagnostic-verdict" style={{
            background: vs.bg, color: vs.fg, padding: "4px 12px", borderRadius: 14,
            fontSize: 11.5, fontWeight: 800,
          }}>
            {vs.label}
          </span>
          <button data-testid="tally-diagnostic-refresh" onClick={onRefresh} style={{ background: "transparent", border: "1px solid #334155", borderRadius: 5, padding: "4px 10px", fontSize: 11.5, color: "#CBD5E1", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}>
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      </div>

      {diag.migration_pending && (
        <div data-testid="tally-diagnostic-migration-banner" style={{ padding: "8px 16px", background: "#FEF3C7", color: "#92400E", fontSize: 11.5, borderBottom: "1px solid #FDE68A" }}>
          <b>Migration pending:</b> apply <code>backend/migrations/tally_diagnostic.sql</code> in Supabase SQL Editor to capture future ingest payloads.
        </div>
      )}

      <div style={{ padding: "12px 16px", borderBottom: "1px solid #E5E7EB", background: "#F8FAFC" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#0F1B2D" }}>{v.label || "—"}</div>
        {v.next_step && <div style={{ fontSize: 12, color: "#475569", marginTop: 4, lineHeight: 1.5 }}>Next step: {v.next_step}</div>}
      </div>

      {/* v1.3.1 — Company + Version + Reports diagnostic strip */}
      <CompanyVersionStrip diag={diag} />

      <div style={{ padding: 16, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        <Stat label="XML received" value={diag.xml_received ? "TRUE" : "FALSE"} valueColor={diag.xml_received ? "#166534" : "#B91C1C"} />
        <Stat label="Total records" value={c.total_records ?? "—"} />
        <Stat label="Agent version" value={cfg.last_ingest_agent_version || cfg.agent_version || "—"} />
        <Stat label="Last ingest" value={cfg.last_ingest_at ? new Date(cfg.last_ingest_at).toLocaleString() : "never"} />
        <Stat label="Cash & Bank ledgers" value={c.cash_accounts ?? 0} />
        <Stat label="Debtors" value={c.debtors ?? 0} />
        <Stat label="Creditors" value={c.creditors ?? 0} />
        <Stat label="Inventory items" value={c.inventory_items ?? 0} />
      </div>

      <div style={{ padding: "0 16px 12px 16px" }}>
        <div style={{ fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>First 20 ledger names</div>
        <div data-testid="tally-diagnostic-ledgers" style={{ marginTop: 6, padding: 10, background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 6, minHeight: 32, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 11.5, color: "#0F1B2D", lineHeight: 1.7 }}>
          {ledgers.length === 0
            ? <span style={{ color: "#94A3B8" }}>(no ledger names — agent reported empty results for every group)</span>
            : ledgers.map((n, i) => <div key={i}>{n}</div>)}
        </div>
      </div>

      <div style={{ padding: "0 16px 16px 16px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ border: "1px solid #E5E7EB", borderRadius: 6, overflow: "hidden" }}>
          <div style={{ padding: "8px 12px", background: "#F8FAFC", fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, borderBottom: "1px solid #E5E7EB" }}>Parsed values (from agent)</div>
          <table data-testid="tally-diagnostic-parsed" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <tbody>
              <Row k="Revenue MTD"    v={fmtINR(p.revenue_mtd)} />
              <Row k="Expenses MTD"   v={fmtINR(p.expenses_mtd)} />
              <Row k="Cash"           v={fmtINR(p.cash)} />
              <Row k="Net profit"     v={fmtINR(p.net_profit)} />
              <Row k="GST payable"    v={fmtINR(p.gst_payable)} />
              <Row k="Inventory value" v={fmtINR(p.inventory_value)} />
            </tbody>
          </table>
        </div>
        <div style={{ border: "1px solid #E5E7EB", borderRadius: 6, overflow: "hidden" }}>
          <div style={{ padding: "8px 12px", background: "#F8FAFC", fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, borderBottom: "1px solid #E5E7EB" }}>Supabase row (financial_snapshots)</div>
          <table data-testid="tally-diagnostic-supabase" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <tbody>
              <Row k="cash"             v={fmtINR(s.cash)} />
              <Row k="revenue_mtd"      v={fmtINR(s.revenue_mtd)} />
              <Row k="expenses_mtd"     v={fmtINR(s.expenses_mtd)} />
              <Row k="net_profit"       v={fmtINR(s.net_profit)} />
              <Row k="gst_payable"      v={fmtINR(s.gst_payable)} />
              <Row k="gross_margin_pct" v={s.gross_margin_pct ?? "—"} />
              <Row k="data_source"      v={s.data_source || "—"} />
              <Row k="synced_at"        v={s.synced_at ? new Date(s.synced_at).toLocaleString() : "—"} />
            </tbody>
          </table>
        </div>
      </div>

      {Object.keys(errs).length > 0 && (
        <div style={{ padding: "0 16px 16px 16px" }}>
          <div style={{ fontSize: 11, color: "#B91C1C", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Parser errors (per step)</div>
          <div data-testid="tally-diagnostic-errors" style={{ marginTop: 6, padding: 10, background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 6, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 11.5, color: "#7F1D1D", lineHeight: 1.6 }}>
            {Object.entries(errs).map(([k, val]) => (
              <div key={k}><b>{k}</b>: {String(val)}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, valueColor }) {
  return (
    <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 6, padding: "10px 12px" }}>
      <div style={{ fontSize: 10.5, color: "#64748B", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 800, color: valueColor || "#0F1B2D", marginTop: 3 }}>{value}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <tr style={{ borderTop: "1px solid #F1F5F9" }}>
      <td style={{ padding: "7px 12px", color: "#475569", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 11.5 }}>{k}</td>
      <td style={{ padding: "7px 12px", color: "#0F1B2D", fontWeight: 700, textAlign: "right" }}>{v}</td>
    </tr>
  );
}


// ---------------------------------------------------------------------------
// CompanyVersionStrip — v1.3.1 enriched diagnostic.
// Surfaces Tally version (with release/build), the discovered company list
// + match confidence, and the list of reports the agent probed.
// ---------------------------------------------------------------------------
function CompanyVersionStrip({ diag }) {
  const disc  = diag.company_discovery || {};
  const vinfo = diag.version_info || {};
  const supported = (diag.payload?.supported_reports) || [];
  const stepsMeta = diag.payload?.steps || {};

  // Nothing useful to show if the agent v1.3.1 hasn't synced yet.
  const hasV131Payload = Boolean(disc.selected || vinfo.release || vinfo.product || supported.length);
  if (!hasV131Payload) {
    return (
      <div style={{ padding: "10px 16px", background: "#FEF3C7", color: "#92400E", fontSize: 11.5, borderBottom: "1px solid #FDE68A" }} data-testid="tally-diagnostic-v131-banner">
        <b>Agent v1.3.1 required</b> for company discovery + report capability + version probe.
        Customer is on <code>{diag.config?.last_ingest_agent_version || diag.config?.agent_version || "an older build"}</code>.
        Have them re-download the install kit.
      </div>
    );
  }

  const score = Number(disc.match_score || 0);
  const scoreStyle =
    score >= 95 ? { bg: "#DCFCE7", fg: "#166534" } :
    score >= 70 ? { bg: "#FEF3C7", fg: "#92400E" } :
    { bg: "#FEE2E2", fg: "#B91C1C" };

  const available = disc.available || [];
  const supportedNames = supported.filter((r) => r.supported).map((r) => r.report);
  const unsupportedNames = supported.filter((r) => !r.supported).map((r) => r.report);

  return (
    <div style={{ padding: 16, borderBottom: "1px solid #E5E7EB", background: "white" }} data-testid="tally-diagnostic-v131">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        <Stat label="Tally version"
              value={(vinfo.version || "—").toUpperCase()}
              valueColor={vinfo.version && vinfo.version !== "unknown" ? "#166534" : "#B91C1C"} />
        <Stat label="Product"  value={vinfo.product || "—"} />
        <Stat label="Release"  value={vinfo.release || "—"} />
        <Stat label="Build"    value={vinfo.build   || "—"} />
      </div>

      <div style={{ marginTop: 14, border: "1px solid #E2E8F0", borderRadius: 8, overflow: "hidden" }}>
        <div style={{ padding: "10px 14px", background: "#F8FAFC", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #E5E7EB" }}>
          <div>
            <div style={{ fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Company match</div>
            <div style={{ fontSize: 13.5, color: "#0F1B2D", fontWeight: 700, marginTop: 3 }}>
              <span data-testid="tally-diag-selected-company">{disc.selected || "—"}</span>
            </div>
            <div style={{ fontSize: 11.5, color: "#475569", marginTop: 2 }}>
              configured: <code>{disc.configured || "—"}</code> · reason: <code>{disc.match_reason || "—"}</code>
            </div>
          </div>
          <span data-testid="tally-diag-match-score" style={{
            background: scoreStyle.bg, color: scoreStyle.fg, padding: "4px 12px", borderRadius: 12,
            fontSize: 12, fontWeight: 800,
          }}>
            {score}% match
          </span>
        </div>
        <div style={{ padding: 12, fontSize: 12 }}>
          <div style={{ color: "#475569", fontSize: 11, marginBottom: 6 }}>
            {available.length === 0
              ? <b style={{ color: "#B91C1C" }}>No companies loaded in Tally — customer must open a company first.</b>
              : <>Loaded companies ({available.length}):</>}
          </div>
          {available.map((c, i) => (
            <div key={i} data-testid={`tally-diag-company-${i}`}
                 style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderTop: i ? "1px solid #F1F5F9" : "none" }}>
              <span style={{ width: 6, height: 6, borderRadius: 999, background: c.name === disc.selected ? "#10B981" : "#CBD5E1" }} />
              <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 11.5, color: "#0F1B2D", fontWeight: c.name === disc.selected ? 700 : 500 }}>
                {c.name}
              </span>
              {c.starting_from && <span style={{ fontSize: 11, color: "#94A3B8" }}>· FY {c.starting_from}–{c.ending_at}</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Supported reports */}
      <div style={{ marginTop: 14, border: "1px solid #E2E8F0", borderRadius: 8, overflow: "hidden" }}>
        <div style={{ padding: "8px 14px", background: "#F8FAFC", fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, borderBottom: "1px solid #E5E7EB" }}>
          Supported reports on this Tally build
        </div>
        <div style={{ padding: 12, display: "flex", flexWrap: "wrap", gap: 6 }} data-testid="tally-diag-supported-reports">
          {supportedNames.map((n) => (
            <span key={n} style={{ background: "#DCFCE7", color: "#166534", padding: "3px 9px", borderRadius: 12, fontSize: 11.5, fontWeight: 600 }}>✓ {n}</span>
          ))}
          {unsupportedNames.map((n) => (
            <span key={n} style={{ background: "#FEE2E2", color: "#B91C1C", padding: "3px 9px", borderRadius: 12, fontSize: 11.5, fontWeight: 600 }}>✗ {n}</span>
          ))}
          {supported.length === 0 && (
            <span style={{ fontSize: 11.5, color: "#94A3B8" }}>(agent did not probe reports)</span>
          )}
        </div>
      </div>

      {/* Extraction method per step */}
      {Object.keys(stepsMeta).length > 0 && (
        <div style={{ marginTop: 14, border: "1px solid #E2E8F0", borderRadius: 8, overflow: "hidden" }}>
          <div style={{ padding: "8px 14px", background: "#F8FAFC", fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, borderBottom: "1px solid #E5E7EB" }}>
            Extraction method used per step
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }} data-testid="tally-diag-extraction-method">
            <tbody>
              {Object.entries(stepsMeta).map(([k, m]) => (
                <tr key={k} style={{ borderTop: "1px solid #F1F5F9" }}>
                  <td style={{ padding: "6px 12px", color: "#475569", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 11.5, width: 90 }}>{k}</td>
                  <td style={{ padding: "6px 12px", color: m?.error ? "#B91C1C" : "#0F1B2D", fontWeight: 600 }}>{m?.method_used || "—"}</td>
                  <td style={{ padding: "6px 12px", color: "#64748B", fontSize: 11 }}>{m?.ledgers_count ?? 0} ledgers · {m?.duration_ms ?? 0}ms</td>
                  <td style={{ padding: "6px 12px", color: "#B91C1C", fontSize: 11, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m?.error || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
